import { SaleOfferBearingVisibility } from '../../models/sale-offer-bearing-visibility';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SaleOfferBuilder } from '../../models/builders/sale-offer-builder';
import { SaleOffer } from '../../models/sale-offer';
import { SaleOfferLine, TYPE_COMPLEMENT, TYPE_OTHER, TYPE_PRODUCT } from '../../models/sale-offer-line';
import { SaleOfferLineProduct } from '../../models/sale-offer-line-product';
import { SaleOfferLineComplement } from '../../models/sale-offer-line-complement';
import { SaleOfferLineOther } from '../../models/sale-offer-line-other';
import { mapCollection } from '../../utils/collection.utils';
import { SaleOffersRequestInterface } from '../../models/request/sale-offers-request.interface';
import { SaleOfferDepartureBuilder } from '../../models/builders/sale-offer-departure-builder';
import { SaleOfferDepartureReasonBuilder } from '../../models/builders/sale-offer-departure-reason.builder';
import { SaleOfferDeparture } from '../../models/sale-offer-departure';
import { SaleOfferDepartureSerialNumber } from '../../models/sale-offer-departure-serial-number';
import { SaleOfferPreparation } from '../../models/sale-offer-preparation';
import { SaleOfferPreparationBuilder } from '../../models/builders/sale-offer-preparation-builder';
import { SaleOfferBearing } from '../../models/sale-offer-bearing';
import { SaleOfferBearingBuilder } from '../../models/builders/sale-offer-bearing-builder';
import { SaleOfferDepartureSerialNumberBuilder } from '../../models/builders/sale-offer-departure-serial-number.builder';
import { SaleOfferControlDocument } from '../../models/sale-offer-control-document';
import { SaleOfferControlDocumentBuilder } from '../../models/builders/sale-offer-control-document-builder';

const createSaleOfferLineOtherBody = (saleOfferLineOther: SaleOfferLineOther) => (
    {
      concept: saleOfferLineOther.concept,
      amount: saleOfferLineOther.amount
    }
);

const createSaleOfferLineComplementBody = (saleOfferLineComplement: SaleOfferLineComplement) => (
    {
      complementHash: saleOfferLineComplement.complementHash,
      totalAmount: saleOfferLineComplement.totalAmount
    }
);

const createSaleOfferLineProductBody = (saleOfferLineProduct: SaleOfferLineProduct) => (
    {
      productHash: saleOfferLineProduct.productHash,
      discountType: saleOfferLineProduct.discountType,
      discountNumber: saleOfferLineProduct.discountNumber,
      discountAppliedAmount: saleOfferLineProduct.discountAppliedAmount,
      commercialMargin: saleOfferLineProduct.commercialMargin,
      amount: saleOfferLineProduct.amount
    }
);

const createSaleOfferLineBody = (saleOfferLine: SaleOfferLine) => (
    {
      hash: saleOfferLine.hash,
      type: saleOfferLine.type,
      saleOfferLineProduct: (saleOfferLine.type === TYPE_PRODUCT) ? createSaleOfferLineProductBody(
          saleOfferLine.saleOfferLineProduct
      ) : null,
      saleOfferLineComplement: (saleOfferLine.type === TYPE_COMPLEMENT) ? createSaleOfferLineComplementBody(
          saleOfferLine.saleOfferLineComplement
      ) : null,
      saleOfferLineOther: (saleOfferLine.type === TYPE_OTHER) ? createSaleOfferLineOtherBody(
          saleOfferLine.saleOfferLineOther
      ) : null
    }
);

const createSaleOfferBody = (saleOffer: SaleOffer) => (
    {
      offerDate: saleOffer.offerDate,
      customerHash: saleOffer.customerHash,
      traderHash: saleOffer.traderHash,
      saleOfferLines: mapCollection((saleOfferLine: SaleOfferLine) => {
        return createSaleOfferLineBody(saleOfferLine);
      }, saleOffer.saleOfferLines),
      observations: saleOffer.observations,
      additionalData: saleOffer.additionalData,
      internalObservations: saleOffer.internalObservations,
      offerObservations: saleOffer.offerObservations,
      paymentMethodHash: saleOffer.paymentMethodHash,
      paymentMethodDescription: saleOffer.paymentMethodDescription,
      personWhoIsGoingToSign: saleOffer.personWhoIsGoingToSign,
      isDeposit: saleOffer.isDeposit,
      subTotalAmount: saleOffer.subTotalAmount,
      vat: saleOffer.vat,
      totalVat: saleOffer.totalVat,
      totalAmount: saleOffer.totalAmount
    }
);

const createSaleOfferDepartureSerialNumberBody = (saleOfferDepartureSerialNumber: SaleOfferDepartureSerialNumber) => (
    {
      saleOfferLineHash: saleOfferDepartureSerialNumber.saleOfferLineHash,
      serialNumber: saleOfferDepartureSerialNumber.serialNumber
    }
);

const saleOfferDepartureBody = (saleOfferDeparture: SaleOfferDeparture) => (
    {
      departureDate: saleOfferDeparture.departureDate,
      authorizingPersonHash: saleOfferDeparture.authorizingPersonHash,
      requestMadeByHash: saleOfferDeparture.requestMadeByHash,
      saleOfferDepartureReasonHash: saleOfferDeparture.saleOfferDepartureReasonHash,
      observations: saleOfferDeparture.observations,
      duration: saleOfferDeparture.duration,
      saleOfferDepartureSerialNumbers: mapCollection((saleOfferDepartureSerialNumber: SaleOfferDepartureSerialNumber) => {
        return createSaleOfferDepartureSerialNumberBody(saleOfferDepartureSerialNumber);
      }, saleOfferDeparture.saleOfferDepartureSerialNumbers),
    }
);

const saleOfferPreparationBody = (saleOfferPreparation: SaleOfferPreparation) => (
    {
      preparationDate: saleOfferPreparation.preparationDate,
      observations: saleOfferPreparation.observations
    }
);


const createSaleOfferLinesVisbility = (saleOfferBearingVisibility: SaleOfferBearingVisibility) => (
    saleOfferBearingVisibility.saleOfferLineHash
);

const saleOfferBearingBody = (saleOfferBearing: SaleOfferBearing) => (
    {
      bearingDate: saleOfferBearing.bearingDate,
      observations: saleOfferBearing.observations,
      loadingCustomerHash: saleOfferBearing.loadingCustomerHash,
      loadingPlace: saleOfferBearing.loadingPlace,
      loadingCustomerNif: saleOfferBearing.loadingCustomerNif,
      unloadingCustomerHash: saleOfferBearing.unloadingCustomerHash,
      unloadingPlace: saleOfferBearing.unloadingPlace,
      unloadingCustomerNif: saleOfferBearing.unloadingCustomerNif,
      saleOfferLineHashes: mapCollection((saleOfferBearingVisibility: SaleOfferBearingVisibility) => {
        return createSaleOfferLinesVisbility(saleOfferBearingVisibility);
      }, saleOfferBearing.saleOfferBearingVisibilities)
    }
);

const saleOfferControlDocumentBody = (saleOfferControlDocument: SaleOfferControlDocument) => (
    {
      saleOfferHash: saleOfferControlDocument.saleOfferHash,
      date: saleOfferControlDocument.date,
      transportOperator: saleOfferControlDocument.transportOperator,
      carrier: saleOfferControlDocument.carrier,
      trailer: saleOfferControlDocument.trailer,
      enrollment: saleOfferControlDocument.enrollment,
      viewLogoInPdf: saleOfferControlDocument.viewLogoInPdf,
      observation: saleOfferControlDocument.observation,
      packingUnits: saleOfferControlDocument.packingUnits
    }
);

const createEmailFieldBody = (email: string) => (
    {
      email: email
    }
);

const createSignedByCustomerDateFieldBody = (signedDate: number) => (
    {
      signedByCustomerDate: signedDate
    }
);

@Injectable()
export class SaleOfferApiService {
  private apiUrl: string;

  constructor(
      private httpClient: HttpClient
  ) {
    this.apiUrl = `${environment.apiUrl}sale-offers`;
  }

  getSaleOffer(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}`)
        .pipe(
            map(SaleOfferBuilder.fromJson)
        );
  }

  getSaleOfferDeparture(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/departure`)
        .pipe(
            map(SaleOfferDepartureBuilder.fromJson)
        );
  }

  getSaleOfferPreparation(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/preparation`)
        .pipe(
            map(SaleOfferPreparationBuilder.fromJson)
        );
  }

  getSaleOfferBearing(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/bearing`)
        .pipe(
            map(SaleOfferBearingBuilder.fromJson)
        );
  }

  getSaleOfferControlDocument(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}/control-document`)
        .pipe(
            map(SaleOfferControlDocumentBuilder.fromJson)
        );
  }

  getSaleOfferDefaults() {
    return this.httpClient.get(`${this.apiUrl}/defaults`).pipe(map(SaleOfferBuilder.fromJson));
  }

  getSaleOffers() {
    return this.httpClient.get(this.apiUrl).pipe(map(SaleOfferBuilder.fromJsonList));
  }

  getSaleOfferDepartureReasons() {
    return this.httpClient.get(`${this.apiUrl}/departure-reasons`).pipe(map(SaleOfferDepartureReasonBuilder.fromJsonList));
  }

  getSaleOfferDepartureSerialNumbers() {
    return this.httpClient.get(`${this.apiUrl}/departure-serial-numbers`).pipe(map(SaleOfferDepartureSerialNumberBuilder.fromJsonList));
  }

  previewSaleOffer(saleOfferHash: string) {
    return this.httpClient.get(`${this.apiUrl}/${saleOfferHash}/pdf-preview`, { responseType: 'arraybuffer' })
        .map((arrayBuffer: ArrayBuffer) => new Uint8Array(arrayBuffer));
  }

  downloadSaleOfferPdf(saleOfferHash: string) {
    return this.httpClient.get(`${this.apiUrl}/${saleOfferHash}/pdf`, { responseType: 'arraybuffer' });
  }

  downloadInvoicePdf(saleOfferHash: string) {
    return this.httpClient.get(`${this.apiUrl}/${saleOfferHash}/invoice-pdf`, { responseType: 'arraybuffer' });
  }

  downloadDeparturePdf(saleOfferHash: string): any {
    return this.httpClient.get(`${this.apiUrl}/${saleOfferHash}/departure-pdf`, { responseType: 'arraybuffer' });
  }

  downloadPreparationPdf(saleOfferHash: string): any {
    return this.httpClient.get(`${this.apiUrl}/${saleOfferHash}/preparation-pdf`, { responseType: 'arraybuffer' });
  }

  downloadBearingPdf(saleOfferHash: string): any {
    return this.httpClient.get(`${this.apiUrl}/${saleOfferHash}/bearing-pdf`, { responseType: 'arraybuffer' });
  }

  downloadControlDocumentPdf(saleOfferHash: string): any {
    return this.httpClient.get(`${this.apiUrl}/${saleOfferHash}/control-document-pdf`, { responseType: 'arraybuffer' });
  }

  finishSaleOffer(saleOfferHash: string) {
    return this.httpClient.post(`${this.apiUrl}/${saleOfferHash}/finish`, {});
  }

  continueToNextStep(saleOfferHash: string) {
    return this.httpClient.post(`${this.apiUrl}/${saleOfferHash}/next-step`, {});
  }

  sendSaleOfferPdfByEmail(saleOfferHash: string, email: string) {
    return this.httpClient.post(`${this.apiUrl}/${saleOfferHash}/pdf-send`, createEmailFieldBody(email));
  }

  setSignedByCustomer(saleOfferHash: string, pickedMillis: number) {
    return this.httpClient.put(`${this.apiUrl}/${saleOfferHash}/signature`, createSignedByCustomerDateFieldBody(pickedMillis));
  }

  getFilteredSaleOffers(request: SaleOffersRequestInterface) {
    let params = new HttpParams();
    params = params.append('page', request.page.toString())
        .append('itemsPerPage', request.itemsPerPage.toString())
        .append('sortField', request.sortField)
        .append('reverse', (!!request.reverse).toString());
    if (request.status) {
      params = params.append('status', request.status.toString());
    }
    if (request.customerHash) {
      params = params.append('customerHash', request.customerHash);
    }
    if (request.traderHash) {
      params = params.append('traderHash', request.traderHash);
    }
    if (request.isDeposit) {
      params = params.append('isDeposit', request.isDeposit.toString());
    }

    return this.httpClient.get(`${this.apiUrl}/filter`, { params: params })
      .pipe(
        map(this.mapData)
    );
  }

  saveSaleOffer(saleOffer: SaleOffer) {
    return this.httpClient.post(`${this.apiUrl}`, createSaleOfferBody(saleOffer)).pipe(
        map(SaleOfferBuilder.fromJson)
    );
  }

  updateSaleOffer(saleOfferHash: string, saleOffer: SaleOffer) {
    return this.httpClient.put(`${this.apiUrl}/${saleOfferHash}`, createSaleOfferBody(saleOffer)).pipe(
        map(SaleOfferBuilder.fromJson)
    );
  }

  createSaleOfferDeparture(saleOfferHash: string, saleOfferDeparture: SaleOfferDeparture) {
    return this.httpClient.post(`${this.apiUrl}/${saleOfferHash}/departure`, saleOfferDepartureBody(saleOfferDeparture)).pipe(
        map(SaleOfferDepartureBuilder.fromJson)
    );
  }

  updateSaleOfferDeparture(saleOfferHash: string, saleOfferDeparture: SaleOfferDeparture) {
    return this.httpClient.put(`${this.apiUrl}/${saleOfferHash}/departure/${saleOfferDeparture.hash}`, saleOfferDepartureBody(
        saleOfferDeparture)).pipe(map(SaleOfferDepartureBuilder.fromJson)
    );
  }

  deleteSaleOfferByHash(saleOfferHash: string) {
    return this.httpClient.delete(`${this.apiUrl}/${saleOfferHash}`);
  }

  createSaleOfferPreparation(saleOfferHash: string, saleOfferPreparation: SaleOfferPreparation) {
    return this.httpClient.post(`${this.apiUrl}/${saleOfferHash}/preparation`, saleOfferPreparationBody(saleOfferPreparation)).pipe(
        map(SaleOfferPreparationBuilder.fromJson)
    );
  }

  updateSaleOfferPreparation(saleOfferHash: string, saleOfferPreparation: SaleOfferPreparation) {
    return this.httpClient.put(`${this.apiUrl}/${saleOfferHash}/preparation/${saleOfferPreparation.hash}`, saleOfferPreparationBody(
        saleOfferPreparation)
    );
  }

  createSaleOfferBearing(saleOfferHash: string, saleOfferBearing: SaleOfferBearing) {
    return this.httpClient.post(`${this.apiUrl}/${saleOfferHash}/bearing`, saleOfferBearingBody(saleOfferBearing)).pipe(
        map(SaleOfferBearingBuilder.fromJson)
    );
  }

  updateSaleOfferBearing(saleOfferHash: string, saleOfferBearing: SaleOfferBearing) {
    return this.httpClient.put(
        `${this.apiUrl}/${saleOfferHash}/bearing/${saleOfferBearing.hash}`,
        saleOfferBearingBody(saleOfferBearing)
    );
  }

  createSaleOfferControlDocument(saleOfferControlDocument: SaleOfferControlDocument) {
    return this.httpClient.post(
        `${this.apiUrl}/${saleOfferControlDocument.saleOfferHash}/control-document`,
        saleOfferControlDocumentBody(saleOfferControlDocument)
    );
  }

  updateSaleOfferControlDocument(saleOfferControlDocument: SaleOfferControlDocument) {
    return this.httpClient.put(
        `${this.apiUrl}/${saleOfferControlDocument.saleOfferHash}/control-document/${saleOfferControlDocument.hash}`,
        saleOfferControlDocumentBody(saleOfferControlDocument)
    );
  }

  private mapData = (data) => {
    data.saleOffers = SaleOfferBuilder.fromJsonList(data.saleOffers);
    return data;
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/ubaristi-app-test/workspace/src/app/core/services/api/sale-offer-api.service.ts